import {
  KdsButtonIcon,
  KdsCardFile,
  KdsInput,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiEditLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { type ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import { OrderItemUpdateCommandDispatchTypeEnum } from "@/config/apiClient";
import { MAX_TOTAL_QUANTITY_LENGHT } from "@/constants/orderItemQuantites.constants";
import { redispatchQuantities } from "@/helpers/redispatchQuantities";
import { type FileAddressBlockProps } from "./FileAddressBlock.types";

export const FileAddressBlock = ({
  onRemoveFile,
  onPressEditVersion,
}: FileAddressBlockProps) => {
  const { t } = useTranslate();

  // Form context
  const methods = useFormContext<OrderItemConfigurationSchema>();

  const handleInputMaxLength = (event: { currentTarget: HTMLInputElement }) => {
    if (event.currentTarget.value.length > MAX_TOTAL_QUANTITY_LENGHT) {
      event.currentTarget.value = event.currentTarget.value.slice(
        0,
        MAX_TOTAL_QUANTITY_LENGHT
      );
    }
  };

  return (
    <div className="flex flex-col gap-sp-md rounded-br-sm bg-elevation-third p-sp-md">
      {/* Quantities and Versions information */}
      <div className="flex items-center gap-sp-xs">
        <div className="flex flex-nowrap items-center gap-sp-xs">
          <KdsText color="text-bodyvariant">
            {t("orderItemConfig.totalQuantity")}
          </KdsText>
          <div className="w-20">
            <KdsInput
              size="xs"
              min={0}
              type="number"
              onInput={handleInputMaxLength}
              {...methods.register("totalQuantity", {
                valueAsNumber: true,
                onChange: (event: ChangeEvent<HTMLInputElement>) =>
                  redispatchQuantities({
                    totalQuantities: Number.parseInt(
                      event.currentTarget.value,
                      10
                    ),
                    methods: methods,
                  }),
                onBlur: (event: ChangeEvent<HTMLInputElement>) => {
                  const val = event.target.value;
                  if (val === "" || Number(val) < 0) {
                    methods.setValue("totalQuantity", 0);
                  }
                },
              })}
            />
          </div>
        </div>
        <KdsText color="text-bodyvariant">|</KdsText>
        <div className="flex flex-nowrap items-center gap-sp-xs">
          <KdsText color="text-bodyvariant">
            {t("orderItemConfig.totalVersion")}
          </KdsText>
          <KdsText size="subtitle-sm">
            {methods.getValues("versions").filter((v) => !v.isDeleted).length}
          </KdsText>
        </div>
        <KdsButtonIcon
          variant="outlined"
          icon={RiEditLine}
          size="small"
          onPress={onPressEditVersion}
        />
      </div>

      {/* Dispatch Action Buttons */}
      <div className="flex flex-col gap-sp-md border-t pt-sp-md">
        <KdsText size="subtitle-sm">
          {t("orderItemConfig.dispatchFile")}
        </KdsText>
        {methods.watch("dispatchFiles")?.map((file, index) => {
          if (
            !methods.watch(`dispatchFiles.${index}.fileName`) ||
            methods.watch(`dispatchFiles.${index}.isDeleted`)
          ) {
            return null;
          }

          return (
            <KdsCardFile
              key={file.blobId ?? file.fileSignedId}
              fileName={methods.watch(`dispatchFiles.${index}.fileName`)}
              fileUrl={methods.watch(`dispatchFiles.${index}.url`)}
              fileSize={methods.watch(`dispatchFiles.${index}.size`)}
              isDeletable
              isViewable={!!methods.watch(`dispatchFiles.${index}.url`)}
              onDelete={() => {
                methods.setValue(`dispatchFiles.${index}.fileSignedId`, "");
                methods.setValue(`dispatchFiles.${index}.isDeleted`, true);
                methods.setValue(
                  "dispatchType",
                  OrderItemUpdateCommandDispatchTypeEnum.SingleDispatch,
                  {
                    shouldDirty: true,
                  }
                );
                onRemoveFile();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
